import moment from "moment";
import { BaseFilters } from "../../../components/filters/FilterToolBar";
import { PaymentMethod } from "../../../components/settings/pages/researcher/PaymentMethodSettings";
import { api, createURLSearchParams } from "../api";
import { Sort as SortDirection } from "../axios";
import { PaymentDetailsAPIResponse, parsePaymentDetails } from "./user/user";

const paymentsUrl = "/v2/payment";

type PaginatedTransactions = {
  transactions: Transaction[];
  count: number;
};

export enum PaymentStatus {
  NONE = 0,
  INITIATED = 1,
  COMPLETE = 2
}

type UserSummaryAPIResponse = {
  user_id: number;
  username: string;
  email: string;
  pfp?: string;
  display_name?: string;
};

type UserSummary = {
  id: number;
  username: string;
  email: string;
  profilePicture?: string;
  displayName?: string;
};

export type TransactionQueryFilters = {
  page?: string;
  limit?: string;
  sort?: SortDirection;
};

export type TransactionAPIResponse = {
  id: number;
  user_id: number;
  report_id: number;
  program_id: number;
  researcher: UserSummaryAPIResponse;
  business: UserSummaryAPIResponse;
  payment_mode_used: string;
  other_payment_details: string;
  amount: number;
  status: PaymentStatus;
  createdAt: string;
  updatedAt: string;
};

export type Transaction = {
  id: number;
  reportId: number;
  programID: number;
  researcher: UserSummary;
  business: UserSummary;
  paymentMethod: string;
  otherDetails: string;
  amount: number;
  status: PaymentStatus;
  createdAt: string;
  updatedAt: string;
};

export type TransactionFilters = {
  page?: number;
  sort?: SortDirection;
} & BaseFilters;

const parseUserSummary = (user: UserSummaryAPIResponse): UserSummary => ({
  id: user.user_id,
  username: user.username,
  email: user.email,
  profilePicture: user.pfp,
  displayName: user.display_name
});

export const parseTransaction = (
  transaction: TransactionAPIResponse
): Transaction => ({
  id: transaction.id,
  reportId: transaction.report_id,
  researcher: parseUserSummary(transaction.researcher),
  business: parseUserSummary(transaction.business),
  programID: transaction.program_id,
  amount: transaction.amount,
  status: transaction.status,
  paymentMethod:
    transaction.payment_mode_used === "bank_account"
      ? PaymentMethod.BANK_ACCOUNT
      : transaction.payment_mode_used === "paypal"
      ? PaymentMethod.PAYPAL
      : transaction.payment_mode_used === "orbit_remix"
      ? PaymentMethod.ORBIT
      : "",
  otherDetails: transaction.other_payment_details,
  createdAt: moment(transaction.createdAt).format("DD/MM/YYYY h:mma"),
  updatedAt: moment(transaction.updatedAt).format("DD/MM/YYYY h:mma")
});

export const paymentsApi = api.injectEndpoints({
  endpoints: builder => ({
    /**
     * Retrieve a list of past and current transactions belonging to the current user
     * (or all transactions if admin)
     */
    getTransactions: builder.query<PaginatedTransactions, TransactionFilters>({
      query: filters => {
        const params = createURLSearchParams<TransactionQueryFilters>({
          ...filters,
          page: filters.page?.toString(),
          limit: filters.limit.toString()
        });

        return { url: paymentsUrl + "?" + params.toString(), method: "get" };
      },
      transformResponse: (response: {
        transactions: TransactionAPIResponse[];
        count: number;
      }) => ({
        transactions: response.transactions.map(transaction =>
          parseTransaction(transaction)
        ),
        count: response.count
      }),
      providesTags: ["Transactions"]
    }),
    /**
     * Retrieve the payment details for a given user (as an admin)
     */
    getPaymentDetails: builder.query<
      { mode: string; details: any } | undefined,
      number
    >({
      query: id => ({
        url: paymentsUrl + "/details?user_id=" + id,
        method: "get"
      }),
      transformResponse: (response: PaymentDetailsAPIResponse) =>
        parsePaymentDetails(response),
      providesTags: ["PaymentDetails"]
    }),
    /**
     * Update the current users payment details (as a researcher)
     */
    setPaymentDetails: builder.mutation<
      void,
      { mode: PaymentMethod; details: unknown }
    >({
      query: ({ mode, details }) => ({
        url: paymentsUrl + "/details",
        method: "post",
        data: {
          mode:
            mode === PaymentMethod.PAYPAL
              ? "paypal"
              : mode === PaymentMethod.BANK_ACCOUNT
              ? "bank_account"
              : mode === PaymentMethod.ORBIT
              ? "orbit_remix"
              : "",
          payment_details: details
        }
      }),
      invalidatesTags: ["PaymentDetails"]
    }),
    /**
     * Mark a payment as complete
     */
    markPayment: builder.mutation<
      void,
      {
        transactionId: number;
        amount: number;
        method: string;
        details: string;
      }
    >({
      query: ({ transactionId, amount, method, details }) => ({
        url: paymentsUrl + "/pay",
        method: "post",
        data: {
          id: transactionId,
          amount,
          method,
          details
        }
      }),
      invalidatesTags: ["Transactions"]
    }),
    /**
     * Initiate a payment to the given researcher for the given report
     */
    initiatePayment: builder.mutation<
      void,
      {
        reportId: number;
        amount: number;
      }
    >({
      query: ({ reportId, amount }) => ({
        url: paymentsUrl + "/initiate",
        method: "post",
        data: {
          report_id: reportId,
          amount
        }
      }),
      invalidatesTags: (_, __, { reportId }) => [
        {
          type: "Report",
          id: reportId
        },
        "Transactions"
      ]
    })
  })
});

export const {
  useGetTransactionsQuery,
  useGetPaymentDetailsQuery,
  useInitiatePaymentMutation,
  useMarkPaymentMutation,
  useSetPaymentDetailsMutation
} = paymentsApi;
